import { useCallback, useMemo } from "react";

import { useLookbookConcepts } from "@/features/lookbook/api";
import { useLineLabel, useProductNameMap } from "@/features/products/catalogApi";

import { type ConceptGuide, useConceptGuides } from "./api";
import {
  groupTitle,
  guideSummary,
  matchGuides,
  type GuideGroup,
  type GuideNames,
  type GuideTarget,
  type ShiftRole,
} from "./lib";

export type ShiftGuides = {
  /** "error" only when nothing was ever loaded; a failed refresh keeps the old list. */
  status: "loading" | "error" | "ready";
  groups: GuideGroup[];
  total: number;
  /** "" when the shift has no guide. */
  summary: string;
  groupTitle: (group: GuideGroup) => string;
  retry: () => void;
};

const NO_GUIDES: readonly ConceptGuide[] = [];

/**
 * The guides a photographer or makeup artist should read before one shift
 * (decision 0128): the library matched against the shift's concept, package and
 * product line, with display names from the catalog. Every read behind it is a
 * shared cached query, so a page of shift cards costs one request per source.
 */
export function useShiftGuides(target: GuideTarget, role: ShiftRole): ShiftGuides {
  const { data, isError, refetch } = useConceptGuides();
  // Inactive concepts too: a shift booked before its concept was retired must
  // still name it.
  const concepts = useLookbookConcepts(undefined, true).data;
  const productNames = useProductNameMap();
  const lineLabel = useLineLabel();

  const names = useMemo<GuideNames>(() => {
    const conceptNames = new Map((concepts ?? []).map((c) => [c.id, c.name]));
    return {
      concept: (id) => conceptNames.get(id) ?? "",
      product: (code) => productNames[code] ?? code,
      line: (code) => lineLabel(code),
    };
  }, [concepts, productNames, lineLabel]);

  const { selectedConcept, productCode, category } = target;
  const groups = useMemo(
    () => matchGuides({ selectedConcept, productCode, category }, data ?? NO_GUIDES, role),
    [data, selectedConcept, productCode, category, role],
  );

  const titleOf = useCallback((group: GuideGroup) => groupTitle(group, names), [names]);
  const retry = useCallback(() => void refetch(), [refetch]);

  return {
    status: data ? "ready" : isError ? "error" : "loading",
    groups,
    total: groups.reduce((n, g) => n + g.guides.length, 0),
    summary: guideSummary(groups, names),
    groupTitle: titleOf,
    retry,
  };
}
